import {
  autoEc2AppCount,
  autoRdsSku,
  DEFAULT_DEPLOYMENT_STACK,
  stackHasEc2App,
  stackHasRds,
  type DeploymentStackConfig,
} from "../lib/deploymentStack";
import type { FeatureFlag, Workload } from "../lib/types";
import { fmtInt } from "./costCopy";
import type { Locale } from "./locale";
import { numberLocaleFor } from "./locale";

export type InfrastructurePlanSection = {
  title: string;
  items: string[];
};

const DAYS_PER_MONTH = 30;

function fmtFixed(n: number, locale: Locale, maxDigits: number): string {
  return n.toLocaleString(numberLocaleFor(locale), {
    maximumFractionDigits: maxDigits,
    minimumFractionDigits: 0,
  });
}

function featureSummary(w: Workload, locale: Locale): string {
  const uk: Record<FeatureFlag, string> = {
    chat: "чат / повідомлення",
    storage: "профілі та дані користувача",
    search: "пошук / історія",
    ai: "AI у запитах",
    realtime: "realtime / WebSocket",
    files: "файли та об'єкти",
  };
  const en: Record<FeatureFlag, string> = {
    chat: "chat / messaging",
    storage: "user profiles & data",
    search: "search / history",
    ai: "AI in requests",
    realtime: "realtime / WebSocket",
    files: "files & objects",
  };
  const m = locale === "uk" ? uk : en;
  if (w.enabledFeatures.length === 0) {
    return locale === "uk" ? "базовий API" : "baseline API";
  }
  return w.enabledFeatures.map((f) => m[f]).join(", ");
}

function vpcPlanItems(locale: Locale, stack: DeploymentStackConfig): string[] {
  const priv = stack.networkExposure === "private";
  if (locale === "uk") {
    if (priv) {
      return [
        "VPC без публічного доступу до застосунку: workload у private subnets; клієнти — VPN, Direct Connect, корпоративна мережа або zero trust, не «відкритий інтернет» до бекенду.",
        "До сервісів AWS (S3, DynamoDB, Secrets Manager) — Gateway/Interface VPC endpoints де можливо, щоб зменшити NAT-трафік і поверхню для витоків.",
        "Вихід до LLM/SaaS у інтернет — через NAT із жорсткими egress-політиками та allowlist URL/DNS; VPC Flow Logs і алерти на аномальний outbound.",
      ];
    }
    return [
      "Окремий VPC з private subnets у щонайменше двох AZ для Lambda та доступу до керованих сервісів.",
      "Публічна «вітрина» трафіку — через API Gateway (керований AWS TLS endpoint), без постійних EC2 у public subnet для HTTP API у типовому serverless-патерні.",
      "Для виходу з private subnets до LLM/SaaS: NAT Gateway або комбінація VPC Interface / Gateway endpoints (S3, DynamoDB тощо) — залежно від політики egress.",
    ];
  }
  if (priv) {
    return [
      "Private-by-design VPC: workloads in private subnets; clients use VPN, Direct Connect, corporate network, or zero trust — not the open internet to your app tier.",
      "Prefer Gateway/Interface VPC endpoints for AWS services (S3, DynamoDB, Secrets Manager) to reduce NAT traffic and egress exposure.",
      "Internet egress for LLM/SaaS via NAT with strict outbound policies and URL/DNS allowlists; VPC Flow Logs and alerts for unusual egress.",
    ];
  }
  return [
    "Dedicated VPC with private subnets in at least two AZs for Lambda and access to managed services.",
    "Public exposure is at API Gateway (managed TLS), not long-lived EC2 in public subnets for HTTP in a typical serverless pattern.",
    "Egress from private subnets to LLM/SaaS: NAT Gateway or a mix of VPC interface/gateway endpoints (S3, DynamoDB, etc.) per your egress policy.",
  ];
}

function edgePlanItems(
  locale: Locale,
  stack: DeploymentStackConfig,
  hasFiles: boolean,
): string[] {
  const priv = stack.networkExposure === "private";
  if (locale === "uk") {
    if (priv) {
      return [
        "Вхід у API — приватний HTTP API Gateway, VPC Lattice, внутрішній ALB/NLB або mesh; уникай публічних DNS на бізнес-API, щоб ендпоінти не «світились» в інтернет.",
        "Якщо потрібен публічний фронт — окремий шар (наприклад CloudFront + S3 для статики), бекенд без публічного маршруту для клієнтів з інтернету.",
        hasFiles
          ? "Файли: приватний S3 + підписані URL або лише через внутрішній API; без публічних bucket policy."
          : "Корпоративний доступ: mTLS, IP allowlists, SG/NACL — не лише WAF на публічному edge.",
      ];
    }
    return [
      "Amazon API Gateway — HTTP API як основний HTTPS endpoint, інтеграція з Lambda.",
      "За потреби: AWS WAF, usage plans / throttling під зловживання.",
      hasFiles
        ? "Для роздачі статики/файлів: Amazon CloudFront перед S3 (кеш, TLS на edge)."
        : "За потреби CloudFront перед API або статикою, якщо додаси публічні asset-и.",
    ];
  }
  if (priv) {
    return [
      "API entry: private HTTP API Gateway, VPC Lattice, internal ALB/NLB, or mesh — avoid public DNS for endpoints that must not be internet-discoverable.",
      "If a public UI is needed, isolate it (e.g. CloudFront + S3 for static) and keep backends off public routes for internet clients.",
      hasFiles
        ? "Files: private S3 with presigned URLs or internal API only; avoid public bucket policies."
        : "Corporate access: mTLS, IP allowlists, SG/NACL — not only WAF on a public edge.",
    ];
  }
  return [
    "Amazon API Gateway — HTTP API as the main HTTPS endpoint, integrated with Lambda.",
    "Optional: AWS WAF, usage plans / throttling for abuse protection.",
    hasFiles
      ? "For static/file delivery: CloudFront in front of S3 (caching, TLS at the edge)."
      : "Add CloudFront in front of APIs or static assets if you serve public assets.",
  ];
}

/** Орієнтовний план інфраструктури під поточний workload (без EC2 у базовій моделі). */
export function buildInfrastructurePlan(
  locale: Locale,
  w: Workload,
  stack: DeploymentStackConfig = DEFAULT_DEPLOYMENT_STACK,
): InfrastructurePlanSection[] {
  const dailyReq = w.usersDaily * w.requestsPerUser;
  const monthlyReq = dailyReq * DAYS_PER_MONTH;
  const avgRps = dailyReq / 86_400;
  const peakRps = Math.max(avgRps * 3, avgRps + 0.01);
  const storageGb = (w.usersDaily * w.storageKbPerUser) / 1_000_000;
  const aiPct = Math.round(w.aiRequestShare * 100);
  const hasAi = w.enabledFeatures.includes("ai") || w.aiRequestShare > 0;
  const hasRealtime = w.enabledFeatures.includes("realtime");
  const hasFiles = w.enabledFeatures.includes("files");
  const feats = featureSummary(w, locale);

  if (locale === "uk") {
    const sections: InfrastructurePlanSection[] = [
      {
        title: "Мережа (VPC)",
        items: vpcPlanItems("uk", stack),
      },
      {
        title: "Точка входу (edge)",
        items: edgePlanItems("uk", stack, hasFiles),
      },
      {
        title: "Compute (у цій оцінці — Lambda)",
        items: [
          "Основний compute — AWS Lambda (x86), ~512 MB RAM на виклик — як у формулі вартості в калькуляторі.",
          `Навантаження орієнтовно ${fmtInt(dailyReq, locale)} запитів/день (~${fmtInt(monthlyReq, locale)}/міс) → ~${fmtFixed(avgRps, locale, 2)} req/s у середньому, пік орієнтовно ~${fmtFixed(peakRps, locale, 2)} req/s (спрощено ×3 до середнього).`,
          "Під SLA на піках: зарезервований concurrency, provisioned concurrency або рознесення по кількох функціях.",
          stack.backend === "ec2"
            ? `Бекенд на EC2 у стеку: ${stack.ec2AppAutoCount ? `~${autoEc2AppCount(w.usersDaily)} інстансів (авто від DAU)` : `${stack.ec2AppCount} × ${stack.ec2AppSku}`} — див. breakdown. Lambda у моделі зменшена до мінімуму.`
            : stack.backend === "eks"
              ? "Бекенд на EKS — див. рядок кластера; Lambda лишається як невеликий допоміжний шар у формулі."
              : "Якщо потрібні контейнери на EC2/ECS — обери «EC2» або «EKS» у блоці стеку.",
        ],
      },
      {
        title: "Дані",
        items: [
          `Amazon DynamoDB (on-demand у моделі): таблиці під профіль фіч: ${feats}.`,
          `Орієнтовний обсяг користувацьких записів (метадані/повідомлення) ~${fmtFixed(Math.max(storageGb, 0.001), locale, 3)} GB активного сховища (оцінка з workload).`,
          w.enabledFeatures.includes("search")
            ? "Для пошуку: Global Secondary Indexes або окрема таблиця під пошукові ключі / повнотекстовий рушій за потреби."
            : "За потреби GSI для доступу за альтернативними ключами.",
          hasFiles
            ? "Amazon S3 — об'єктне сховище для файлів; політики lifecycle (IA/Glacier), шифрування SSE-KMS/S3, доступ через presigned URLs або через API."
            : "S3 — за потреби для бекапів, експорту або майбутніх файлів.",
        ],
      },
      {
        title: "Спостереження та безпека",
        items: [
          "Amazon CloudWatch — логи Lambda, метрики API Gateway, dashboards; retention логів за політикою.",
          "IAM roles для Lambda (least privilege), окремі політики на DynamoDB/S3.",
          hasAi
            ? "Секрети API LLM — AWS Secrets Manager або SSM Parameter Store; без ключів у коді чи env у репозиторії."
            : "Секрети та параметри — Secrets Manager / SSM за потреби.",
        ],
      },
    ];

    if (hasRealtime) {
      sections.splice(4, 0, {
        title: "Realtime",
        items: [
          "API Gateway WebSocket API або AWS AppSync (subscriptions) / альтернатива — SNS + полінг, залежно від UX.",
          "З'єднання тримаються окремо від звичайного HTTP API; врахуй ліміти concurrent connections і авторизацію на connect.",
        ],
      });
    }

    if (hasAi) {
      const idx = sections.findIndex((s) => s.title === "Спостереження та безпека");
      const aiSection: InfrastructurePlanSection = {
        title: "Шар LLM",
        items: [
          `Виклики з Lambda до OpenAI / Amazon Bedrock (HTTPS). У workload: ~${aiPct}% запитів з AI, ~${w.avgPromptTokens}/${w.avgCompletionTokens} токенів (prompt/completion) на виклик.`,
          stack.networkExposure === "private"
            ? "Мережа: у приватному VPC трафік до LLM зазвичай через NAT (див. рядок «приватна мережа»); суворий allowlist хостів, без загального 0.0.0.0/0 egress до інтернету. Bedrock — приватні endpoints / PrivateLink за можливості."
            : "Мережа: з private subnet — через NAT або публічний egress за політикою; для Bedrock — перевір підтримку endpoints у регіоні.",
          "Обмеження: таймаути Lambda, retries з backoff, circuit breaker при помилках провайдера.",
        ],
      };
      sections.splice(idx >= 0 ? idx : sections.length, 0, aiSection);
    }

    if (stack.frontend === "cloudfront") {
      const cdnUk: string[] = [
        "Статика та/або API за CloudFront: кеш на edge, TLS, geo restrictions. Origin — S3 або ALB/API Gateway.",
        "Invalidation та miss ratio сильно впливають на вартість; див. рядок CloudFront у breakdown.",
      ];
      if (stack.networkExposure === "private") {
        cdnUk.push(
          "У приватній схемі CloudFront частіше лише для статики або origin у VPC (OAC); не плутай із внутрішнім API без окремого threat model.",
        );
      }
      sections.splice(2, 0, {
        title: "CDN (CloudFront)",
        items: cdnUk,
      });
    }

    if (stack.frontend === "ec2_spa") {
      sections.splice(2, 0, {
        title: "Фронтенд на EC2",
        items: [
          "Один інстанс t3.small-подібний для nginx/vite build — у рядку «EC2 — SPA». Для продакшену зазвичай CloudFront + S3.",
          "Сертифікати Let's Encrypt / ACM на ALB; без ALB у спрощеному рядку.",
        ],
      });
    }

    if (stack.backend === "ec2") {
      sections.push({
        title: "EC2 бекенд (ASG орієнтир)",
        items: [
          `${stack.ec2AppAutoCount ? `~${autoEc2AppCount(w.usersDaily)} × ${stack.ec2AppSku} (авто від DAU)` : `${stack.ec2AppCount} × ${stack.ec2AppSku}`}, on-demand. Private subnets + ALB.`,
          "Auto Scaling policies, health checks, rolling deploy — стандартний патерн.",
        ],
      });
    }

    if (stack.backend === "eks") {
      sections.push({
        title: "EKS",
        items: [
          "Control plane + worker nodes (орієнтир у breakdown). Ingress (ALB/NLB Controller), CNI, cluster autoscaler — плануй окремо.",
          "Pod density та node SKUs сильно змінюють суму; рядок — мінімальний кістяк.",
        ],
      });
    }

    if (stackHasRds(stack)) {
      const rdsId = stack.rdsAutoSize ? autoRdsSku(w.usersDaily) : stack.rdsSku;
      sections.push({
        title: "Amazon RDS (стек)",
        items: [
          `${rdsId}, ${stack.rdsStorageGb} GB gp3${stack.rdsMultiAz ? ", Multi-AZ у формулі" : ""}. ${stack.rdsAutoSize ? "Клас авто від DAU." : "Клас вручну."}`,
          "DynamoDB у моделі зменшена, якщо основна БД — RDS; при «обидві» — поділ навантаження між сервісами.",
        ],
      });
    }

    if (stack.route53Enabled) {
      sections.push({
        title: "DNS та TLS (Route 53)",
        items: [
          "Amazon Route 53: hosted zone, A/AAAA/CNAME для сервісу; орієнтир запитів — у breakdown.",
          "AWS Certificate Manager: публічні сертифікати для ALB, CloudFront, API Gateway зазвичай безкоштовні; приватний PKI в ACM — окремий бюджет.",
          "Health checks, latency-based routing і weighted policies збільшують вартість — заклади окремо для продакшену.",
        ],
      });
    }

    if (stack.albWafEnabled) {
      sections.push({
        title: "Балансер і WAF",
        items: [
          "Application Load Balancer + AWS WAF — спрощений рядок у breakdown (LCU, web ACL, обсяг запитів).",
          "Типово для EC2/EKS за ALB або захисту публічного edge; WAF можна прив’язати й до API Gateway — модель лишається орієнтиром.",
          "Managed rule groups і custom rules сильно змінюють суму; увімкай за моделлю загроз, не всі правила одразу.",
        ],
      });
    }

    if (stack.guardDutyEnabled) {
      sections.push({
        title: "GuardDuty",
        items: [
          "Amazon GuardDuty: кореляція VPC Flow Logs, DNS і CloudTrail — орієнтир у breakdown.",
          "Організації з кількома акаунтами: delegated admin і політики — поза спрощеним рядком.",
          "Malware Protection для S3/EKS, інтеграції з Security Hub — окремі продукти й витрати.",
        ],
      });
    }

    const oosUk: string[] = [];
    if (!stackHasEc2App(stack)) {
      oosUk.push(
        "Виділений EC2 бекенд не обраний — обери «EC2» у стеку або оціни ASG окремо (EBS, ALB, NAT).",
      );
    } else {
      oosUk.push(
        `EC2 бекенд у стеку (${stack.ec2AppAutoCount ? "авто-кількість" : stack.ec2AppCount} × ${stack.ec2AppSku}). EBS, ALB, NAT — додай окремо.`,
      );
    }
    if (!stackHasRds(stack)) {
      oosUk.push(
        "RDS не обрано — лише DynamoDB у моделі; додай RDS у стеку за потреби.",
      );
    } else {
      oosUk.push(
        `RDS у стеку (${stack.rdsAutoSize ? autoRdsSku(w.usersDaily) : stack.rdsSku}, ${stack.rdsStorageGb} GB). Replicas, бекапи понад free tier — окремо.`,
      );
    }
    if (stack.networkExposure === "private") {
      oosUk.push(
        "Детальний розклад VPC Endpoints за сервісом, Transit Gateway, PrivateLink до SaaS — поза спрощеним рядком «приватна мережа».",
      );
    }
    oosUk.push(
      "ElastiCache, MSK, OpenSearch — не в калькуляторі; додаткові worker-ноди / Fargate для EKS — поза спрощеним рядком EKS.",
    );

    sections.push({
      title: "Що не входить у базову модель / обмеження рядків",
      items: oosUk,
    });

    return sections;
  }

  const sections: InfrastructurePlanSection[] = [
    {
      title: "Network (VPC)",
      items: vpcPlanItems("en", stack),
    },
    {
      title: "Edge / entry",
      items: edgePlanItems("en", stack, hasFiles),
    },
    {
      title: "Compute (Lambda in this estimate)",
      items: [
        "Primary compute is AWS Lambda (x86), ~512 MB per invocation — aligned with the cost model.",
        `Rough load ${fmtInt(dailyReq, locale)} requests/day (~${fmtInt(monthlyReq, locale)}/mo) → ~${fmtFixed(avgRps, locale, 2)} req/s average, peak ~${fmtFixed(peakRps, locale, 2)} req/s (simplified ×3 average).`,
        "For peak SLA: reserved concurrency, provisioned concurrency, or split into multiple functions.",
        stack.backend === "ec2"
          ? `EC2 backend in stack: ${stack.ec2AppAutoCount ? `~${autoEc2AppCount(w.usersDaily)} instances (auto from DAU)` : `${stack.ec2AppCount} × ${stack.ec2AppSku}`} — see breakdown. Lambda in the model is minimized.`
          : stack.backend === "eks"
            ? "EKS backend — see cluster line; Lambda stays as a small auxiliary slice in the formula."
            : "Pick EC2 or EKS in the stack block if you need containers.",
      ],
    },
    {
      title: "Data",
      items: [
        `Amazon DynamoDB (on-demand in the model): tables shaped for: ${feats}.`,
        `Approximate user data footprint ~${fmtFixed(Math.max(storageGb, 0.001), locale, 3)} GB from workload inputs (metadata/messages).`,
        w.enabledFeatures.includes("search")
          ? "For search: GSIs or a dedicated table for search keys / optional full-text stack."
          : "Add GSIs if you need alternate access patterns.",
        hasFiles
          ? "Amazon S3 for objects; lifecycle (IA/Glacier), SSE-KMS/S3 encryption, presigned URLs or API-mediated access."
          : "S3 optional for backups, exports, or future file features.",
      ],
    },
    {
      title: "Observability & security",
      items: [
        "Amazon CloudWatch — Lambda logs, API Gateway metrics, dashboards; set log retention per policy.",
        "IAM roles for Lambda (least privilege), scoped policies for DynamoDB/S3.",
        hasAi
          ? "LLM API keys — Secrets Manager or SSM Parameter Store; never in source control."
          : "Secrets and parameters via Secrets Manager / SSM as needed.",
      ],
    },
  ];

  if (hasRealtime) {
    sections.splice(4, 0, {
      title: "Realtime",
      items: [
        "API Gateway WebSocket API or AWS AppSync (subscriptions), or SNS + polling depending on UX.",
        "Connections are separate from HTTP APIs; plan concurrent connection limits and auth on connect.",
      ],
    });
  }

  if (hasAi) {
    const idx = sections.findIndex((s) => s.title === "Observability & security");
    const aiSection: InfrastructurePlanSection = {
      title: "LLM layer",
      items: [
        `Calls from Lambda to OpenAI / Amazon Bedrock (HTTPS). Workload: ~${aiPct}% of requests use AI, ~${w.avgPromptTokens}/${w.avgCompletionTokens} tokens (prompt/completion) per call.`,
        stack.networkExposure === "private"
          ? "Networking: in a private VPC, LLM egress usually crosses NAT (see private-network line); strict host allowlists, avoid broad 0.0.0.0/0 internet egress. Prefer Bedrock private endpoints / PrivateLink where available."
          : "Networking: from private subnets via NAT or public egress per policy; check Bedrock availability in your region.",
        "Guardrails: Lambda timeouts, retries with backoff, circuit breaking on provider errors.",
      ],
    };
    sections.splice(idx >= 0 ? idx : sections.length, 0, aiSection);
  }

  if (stack.frontend === "cloudfront") {
    const cdnEn: string[] = [
      "Static and/or API behind CloudFront: edge cache, TLS, geo rules. Origins — S3 or ALB/API Gateway.",
      "Invalidations and cache miss ratio drive cost; see CloudFront line in breakdown.",
    ];
    if (stack.networkExposure === "private") {
      cdnEn.push(
        "In private designs, CloudFront is often static-only or VPC origins (OAC); do not mix casually with internal APIs without a clear threat model.",
      );
    }
    sections.splice(2, 0, {
      title: "CDN (CloudFront)",
      items: cdnEn,
    });
  }

  if (stack.frontend === "ec2_spa") {
    sections.splice(2, 0, {
      title: "Frontend on EC2",
      items: [
        "One t3.small-class host for nginx / built SPA — see “EC2 — SPA” line. Production often uses CloudFront + S3.",
        "Certs via ACM on ALB; ALB not itemized in the simple line.",
      ],
    });
  }

  if (stack.backend === "ec2") {
    sections.push({
      title: "EC2 backend (ASG indicative)",
      items: [
        `${stack.ec2AppAutoCount ? `~${autoEc2AppCount(w.usersDaily)} × ${stack.ec2AppSku} (auto from DAU)` : `${stack.ec2AppCount} × ${stack.ec2AppSku}`}, on-demand. Private subnets + ALB.`,
        "ASG policies, health checks, rolling deploys — standard pattern.",
      ],
    });
  }

  if (stack.backend === "eks") {
    sections.push({
      title: "EKS",
      items: [
        "Control plane + workers (indicative in breakdown). Ingress (ALB/NLB controller), CNI, cluster autoscaler — plan separately.",
        "Pod density and node SKUs move cost a lot; the line is a minimal skeleton.",
      ],
    });
  }

  if (stackHasRds(stack)) {
    const rdsId = stack.rdsAutoSize ? autoRdsSku(w.usersDaily) : stack.rdsSku;
    sections.push({
      title: "Amazon RDS (stack)",
      items: [
        `${rdsId}, ${stack.rdsStorageGb} GB gp3${stack.rdsMultiAz ? ", Multi-AZ in formula" : ""}. ${stack.rdsAutoSize ? "Class auto from DAU." : "Manual class."}`,
        "DynamoDB in the model is reduced when RDS is primary; with “both”, split traffic between services.",
      ],
    });
  }

  if (stack.route53Enabled) {
    sections.push({
      title: "DNS & TLS (Route 53)",
      items: [
        "Amazon Route 53: hosted zone and A/AAAA/CNAME for the service; query baseline is in the breakdown.",
        "AWS Certificate Manager: public certs for ALB, CloudFront, and API Gateway are usually free; private ACM PKI is a separate budget.",
        "Health checks, latency routing, and weighted policies add cost — plan explicitly for production.",
      ],
    });
  }

  if (stack.albWafEnabled) {
    sections.push({
      title: "Load balancer & WAF",
      items: [
        "Application Load Balancer + AWS WAF — one simplified breakdown line (LCU, web ACL, request volume).",
        "Typical for EC2/EKS behind ALB or protecting a public edge; WAF can attach to API Gateway too — still treat the line as indicative.",
        "Managed rule groups and custom rules move cost a lot; enable what matches your threat model.",
      ],
    });
  }

  if (stack.guardDutyEnabled) {
    sections.push({
      title: "GuardDuty",
      items: [
        "Amazon GuardDuty: correlates VPC Flow Logs, DNS, and CloudTrail — indicative line in the breakdown.",
        "Multi-account organizations: delegated admin policies — beyond the simple line.",
        "Malware Protection for S3/EKS and Security Hub integrations are separate products and spend.",
      ],
    });
  }

  const oosEn: string[] = [];
  if (!stackHasEc2App(stack)) {
    oosEn.push(
      "EC2 backend not selected — pick EC2 in the stack or estimate ASG separately (EBS, ALB, NAT).",
    );
  } else {
    oosEn.push(
      `EC2 backend in stack (${stack.ec2AppAutoCount ? "auto count" : stack.ec2AppCount} × ${stack.ec2AppSku}). EBS, ALB, NAT — add separately.`,
    );
  }
  if (!stackHasRds(stack)) {
    oosEn.push(
      "RDS not selected — DynamoDB-only model; add RDS in the stack if needed.",
    );
  } else {
    oosEn.push(
      `RDS in stack (${stack.rdsAutoSize ? autoRdsSku(w.usersDaily) : stack.rdsSku}, ${stack.rdsStorageGb} GB). Replicas, backups beyond free tier — separate.`,
    );
  }
  if (stack.networkExposure === "private") {
    oosEn.push(
      "Per-service VPC endpoints, Transit Gateway, PrivateLink to SaaS — beyond the simplified private-network line.",
    );
  }
  oosEn.push(
    "ElastiCache, MSK, OpenSearch are not modeled; extra EKS workers / Fargate profiles are beyond the simple EKS line.",
  );

  sections.push({
    title: "Out of scope / line-item limits",
    items: oosEn,
  });

  return sections;
}
